import { useCallback, useEffect, useRef, useState } from 'react';
import { useNavigate } from 'react-router-dom';

type PendingAction = { type: 'url'; url: string } | { type: 'back' };

interface UseUnsavedChangesReturn {
  /** Call to navigate to a URL. Shows dialog if dirty. */
  attemptNavigation: (url: string) => void;
  /** Call to go back. Shows dialog if dirty. */
  attemptBack: () => void;
  /** Whether the confirmation dialog is open */
  showDialog: boolean;
  /** Confirm leaving — executes the pending navigation */
  onConfirmLeave: () => void;
  /** Cancel — closes the dialog and stays on page */
  onCancelDialog: () => void;
}

/**
 * Hook that guards against losing unsaved changes.
 *
 * - Registers `beforeunload` when dirty (browser tab close / refresh)
 * - Registers `popstate` when dirty (browser back / forward)
 * - Provides `attemptNavigation` and `attemptBack` that either navigate
 *   immediately (clean) or open a confirmation dialog (dirty)
 */
export function useUnsavedChanges(isDirty: boolean): UseUnsavedChangesReturn {
  const navigate = useNavigate();
  const [showDialog, setShowDialog] = useState(false);
  const [pendingAction, setPendingAction] = useState<PendingAction | null>(null);
  const isNavigatingRef = useRef(false);

  // --- beforeunload: browser tab close / refresh ---
  useEffect(() => {
    if (!isDirty) return;

    const handleBeforeUnload = (e: BeforeUnloadEvent) => {
      e.preventDefault();
      e.returnValue = '';
    };

    window.addEventListener('beforeunload', handleBeforeUnload);
    return () => window.removeEventListener('beforeunload', handleBeforeUnload);
  }, [isDirty]);

  // --- popstate: browser back / forward ---
  useEffect(() => {
    if (!isDirty) return;

    const handlePopState = () => {
      if (isNavigatingRef.current) return;
      // Cancel the navigation by pushing the current state back
      window.history.pushState(null, '', window.location.href);
      setPendingAction({ type: 'back' });
      setShowDialog(true);
    };

    window.addEventListener('popstate', handlePopState);
    return () => window.removeEventListener('popstate', handlePopState);
  }, [isDirty]);

  const executeAction = useCallback(
    (action: PendingAction) => {
      isNavigatingRef.current = true;
      if (action.type === 'back') {
        navigate(-1);
      } else {
        navigate(action.url);
      }
      // Reset flag after navigation completes
      setTimeout(() => {
        isNavigatingRef.current = false;
      }, 100);
    },
    [navigate]
  );

  const attemptNavigation = useCallback(
    (url: string) => {
      if (!isDirty) {
        navigate(url);
        return;
      }
      setPendingAction({ type: 'url', url });
      setShowDialog(true);
    },
    [isDirty, navigate]
  );

  const attemptBack = useCallback(() => {
    if (!isDirty) {
      navigate(-1);
      return;
    }
    setPendingAction({ type: 'back' });
    setShowDialog(true);
  }, [isDirty, navigate]);

  const onConfirmLeave = useCallback(() => {
    setShowDialog(false);
    if (pendingAction) {
      executeAction(pendingAction);
    }
    setPendingAction(null);
  }, [pendingAction, executeAction]);

  const onCancelDialog = useCallback(() => {
    setShowDialog(false);
    setPendingAction(null);
  }, []);

  return {
    attemptNavigation,
    attemptBack,
    showDialog,
    onConfirmLeave,
    onCancelDialog,
  };
}
